import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Box, Typography, Menu, MenuItem, Snackbar, Alert, useMediaQuery } from "@mui/material";
import { validateDonationAmount } from "../../../utils/donationValidation";
import { fetchCmsHomeAction } from "../../../store/slices/cmsSlice";
import SubscribeDetailsStep from "../DonationBoxV2/SubscribeDetailsStep";
import { HINDI_FONT } from "../../../pages/Temples/shared";
import onceHeart from "../../../assets/Images/Temples/donation-box/once-heart.svg";
import onceHeartInactive from "../../../assets/Images/Temples/donation-box/once-heart-inactive.svg";
import monthlyRepeat from "../../../assets/Images/Temples/donation-box/monthly-repeat.svg";
import monthlyRepeatActive from "../../../assets/Images/Temples/donation-box/monthly-repeat-active.svg";
import currencyChevron from "../../../assets/Images/Temples/donation-box/currency-chevron.svg";
import chevronDown from "../../../assets/Images/Temples/donation-box/chevron-down.svg";
import prayingHands from "../../../assets/Images/Temples/donation-box/praying-hands.svg";
import phonepeIcon from "../../../assets/Images/Temples/donation-box/phonepe.svg";
import gpayCircle from "../../../assets/Images/Temples/donation-box/gpay-circle.svg";
import gpayLogo from "../../../assets/Images/Temples/donation-box/gpay-logo.svg";
import bhimUpiIcon from "../../../assets/Images/Temples/donation-box/bhim-upi.svg";
import moreCircle from "../../../assets/Images/Temples/donation-box/more-circle.svg";
import moreDots from "../../../assets/Images/Temples/donation-box/more-dots.svg";

// Temple donation box (the "Start Monthly Giving" modal on /temples), built to the
// Website-Revamp Figma "Donation Box" (node 3230:6815).
//
// Monthly by default; "Donate Once" appears only when admin turns it on (Temples → Monthly
// Donation Box). Where the money goes:
//   Monthly → the Temple sub-category picked in "My contribution goes to" (admin → Categories),
//             as the subscription's donation_category — never a campaign.
//   Once    → no picker. On a temple campaign page (campaignId set) it is credited to that
//             campaign; elsewhere (/temples) to the default temple fund = the first active
//             sub-category in admin order.
// The fee row belongs to one-time gifts, so it only shows on the Once tab. "Donate via" shows on both
// tabs; each option continues the current tab's flow (one-time payment or monthly subscription).
// DonationBoxV2 (Life) is unchanged.

const RED = "#EC1B26";
const INK = "#1D1D1B";
const MUTED = "#7D7D7D";
const GOLD_LIGHT = "#F3D27A";
const MAROON = "#66040B";
const FONT = "'Red Hat Display', sans-serif";
// The design sets ₹ in Inter (Red Hat Display's ₹ differs); falls back cleanly.
const RUPEE_FONT = "Inter, 'Red Hat Display', sans-serif";

const INR = { code: "INR", symbol: "₹" };
const SUPPORT_PCTS = [12, 15, 20, 25, 0];

// Checkout step (SubscribeDetailsStep) in this box's colours: red button, cream amount bar.
const DETAILS_THEME = {
  accent: RED,
  link: RED,
  amountBg: "#FBF5EC",
  amountBorder: "rgba(201, 149, 43, 0.4)",
  amountTag: "#C9952B",
  button: RED,
  buttonRadius: "12px",
  buttonShadow: "0px 10px 24px 0px rgba(236, 27, 38, 0.35)",
  buttonShadowActive: "0px 6px 16px 0px rgba(236, 27, 38, 0.35)",
};

// Used when the page passes no config; matches the seeded CMS section.
export const DEFAULT_TEMPLE_BOX_CONFIG = {
  tagline: "॥ दान ॥",
  title: "Offer Your Seva",
  subtitle: "Every rupee goes to this temple's restoration",
  donationTypes: "monthly", // monthly / once / both — the toggle only shows with both
  defaultMode: "monthly",
  contributionLabel: "My contribution goes to:",
  buttonLabel: "Continue Donation",
  monthlyLabel: "Monthly",
  defaultAmount: 2100,
  amounts: [
    { amount: 501, label: "" },
    { amount: 1100, label: "" },
    { amount: 2100, label: "Most Donated" },
    { amount: 5100, label: "" },
    { amount: 11000, label: "" },
  ],
};

const Rupee = ({ value, sx }) => (
  <Box component="span" sx={sx}>
    <Box component="span" sx={{ fontFamily: RUPEE_FONT }}>₹</Box>
    {Number(value || 0).toLocaleString("en-IN")}
  </Box>
);

const PAYMENT_OPTIONS = [
  { name: "PhonePe", icon: <Box component="img" src={phonepeIcon} alt="" sx={{ width: 40, height: 40 }} /> },
  {
    name: "Gpay",
    icon: (
      <Box sx={{ position: "relative", width: 40, height: 40 }}>
        <Box component="img" src={gpayCircle} alt="" sx={{ width: 40, height: 40 }} />
        <Box component="img" src={gpayLogo} alt="" sx={{ position: "absolute", left: "50%", top: "50%", transform: "translate(-50%, -45%)", width: 32.13, height: 12.8 }} />
      </Box>
    ),
  },
  { name: "Bhim UPI", icon: <Box component="img" src={bhimUpiIcon} alt="" sx={{ width: 41, height: 40 }} /> },
  {
    name: "More",
    icon: (
      <Box sx={{ position: "relative", width: 39.34, height: 40 }}>
        <Box component="img" src={moreCircle} alt="" sx={{ width: 39.34, height: 40 }} />
        <Box component="img" src={moreDots} alt="" sx={{ position: "absolute", left: "50%", top: "50%", transform: "translate(-50%, -50%)", width: 18.08, height: 4.47 }} />
      </Box>
    ),
  },
];

const SmallCaps = ({ children, sx }) => (
  <Typography sx={{ fontFamily: FONT, fontWeight: 700, fontSize: "11px", lineHeight: "14px", letterSpacing: "1.5px", color: MUTED, textTransform: "uppercase", ...sx }}>
    {children}
  </Typography>
);

const TempleDonationBox = ({
  // [{ code, label }] — active Temple sub-categories, in admin order
  subCategories = [],
  // see DEFAULT_TEMPLE_BOX_CONFIG
  config = null,
  campaignTitle = "Temple Seva",
  // Temple campaign page only: one-time gifts are credited to this campaign.
  campaignId = null,
  // Temple campaign page only: the campaign being viewed, so a successful payment opens the
  // temple thank-you page (monthly gifts carry no campaign of their own).
  sourceCampaignId = null,
  // Optional: called with { kind, frequency, amount, tipAmount, form, additionalData } when the
  // donor closes the Razorpay checkout without paying. The page uses it for the reminder popup.
  // Without it, closing the checkout goes to the failure page as before.
  onCheckoutAbandoned = null,
}) => {
  const dispatch = useDispatch();
  const cmsHome = useSelector((state) => state.cms.home);
  const isMobile = useMediaQuery("(max-width:600px)");

  const cfg = { ...DEFAULT_TEMPLE_BOX_CONFIG, ...(config || {}) };
  const amountButtons = (Array.isArray(cfg.amounts) && cfg.amounts.length ? cfg.amounts : DEFAULT_TEMPLE_BOX_CONFIG.amounts).slice(0, 5);
  const chipAmounts = amountButtons.map((a) => a.amount);
  const initialAmount = Number(cfg.defaultAmount) > 0 ? Number(cfg.defaultAmount) : chipAmounts[0];
  const donationTypes = ["monthly", "once", "both"].includes(cfg.donationTypes) ? cfg.donationTypes : "monthly";
  const showToggle = donationTypes === "both";

  const [mode, setMode] = useState(
    donationTypes === "once" || (showToggle && cfg.defaultMode === "once") ? "once" : "monthly"
  );
  const isOnce = donationTypes === "once" || (showToggle && mode === "once");

  const [baseAmount, setBaseAmount] = useState(initialAmount);
  const [selectedChipIdx, setSelectedChipIdx] = useState(() => {
    const idx = chipAmounts.indexOf(initialAmount);
    return idx >= 0 ? idx : null;
  });
  const [isOtherMode, setIsOtherMode] = useState(chipAmounts.indexOf(initialAmount) < 0);
  const [supportPct, setSupportPct] = useState(12);
  const [supportMenuAnchor, setSupportMenuAnchor] = useState(null);
  const [selectedContributionIdx, setSelectedContributionIdx] = useState(0);
  const [contributionMenuAnchor, setContributionMenuAnchor] = useState(null);
  const [detailsStep, setDetailsStep] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");

  useEffect(() => {
    // The minimum-amount rule lives in the shared CMS.
    if (!cmsHome) dispatch(fetchCmsHomeAction());
  }, [cmsHome, dispatch]);

  const contributionOptions = subCategories.map((s) => s.label);
  const contributionCategoryCodes = subCategories.map((s) => s.code);
  const hasSubCategories = contributionCategoryCodes.length > 0;
  const safeIdx = Math.min(selectedContributionIdx, Math.max(0, contributionOptions.length - 1));
  const selectedDonationCategory = contributionCategoryCodes[safeIdx] || null;
  const selectedLabel = contributionOptions[safeIdx] || campaignTitle;
  // Once on a campaign page needs no temple fund; everything else does.
  const onceToCampaign = isOnce && Boolean(campaignId);

  // Platform support fee only on one-time gifts.
  const supportAmount = isOnce ? Math.round((baseAmount * supportPct) / 100) : 0;
  const totalAmount = baseAmount + supportAmount;

  const selectChip = (idx) => {
    setSelectedChipIdx(idx);
    setIsOtherMode(false);
    setBaseAmount(chipAmounts[idx]);
  };
  const enterOtherMode = () => {
    setSelectedChipIdx(null);
    setIsOtherMode(true);
  };
  const handleAmountInput = (e) => {
    const val = Number(e.target.value.replace(/[^0-9]/g, "")) || 0;
    setBaseAmount(val);
    const idx = chipAmounts.indexOf(val);
    if (idx >= 0) { setSelectedChipIdx(idx); setIsOtherMode(false); }
    else setSelectedChipIdx(null);
  };

  const showError = (message) => {
    setSnackbarMessage(message);
    setSnackbarOpen(true);
  };

  const handleContinue = () => {
    if (!onceToCampaign && !hasSubCategories) {
      showError("Temple giving isn't open yet. Please check back soon.");
      return;
    }
    const v = validateDonationAmount(totalAmount, cmsHome, false, INR.symbol);
    if (!v.isValid) {
      showError(v.error);
      return;
    }
    // target: the campaign or temple fund this payment is credited to
    const target = onceToCampaign
      ? { campaignId, category: null, label: campaignTitle }
      : isOnce
        ? { campaignId: null, category: contributionCategoryCodes[0], label: contributionOptions[0] }
        : { campaignId: null, category: selectedDonationCategory, label: selectedLabel };
    setDetailsStep(
      isOnce
        ? { amount: totalAmount, frequency: "once", tipAmount: supportAmount, ...target }
        : { amount: baseAmount, frequency: "monthly", tipAmount: 0, ...target }
    );
  };

  const amountDisplay = isOtherMode ? String(baseAmount || "") : Number(baseAmount).toLocaleString("en-IN");

  // ── Shared shell: header + white body ───────────────────────────────────────
  const shell = (body) => (
    <Box
      sx={{
        background: "#FFFFFF",
        border: "1px solid #EFE4D3",
        borderRadius: "20px",
        boxShadow: "0px 18px 40px 0px rgba(102, 13, 13, 0.1)",
        overflow: "hidden",
        width: "100%",
        boxSizing: "border-box",
      }}
    >
      <Box
        sx={{
          background: `linear-gradient(to right, ${MAROON}, #C9222F)`,
          px: "24px",
          py: "16px",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: "4px",
          textAlign: "center",
        }}
      >
        {cfg.tagline && (
          <Typography sx={{ fontFamily: HINDI_FONT, fontSize: "18px", lineHeight: "24px", color: GOLD_LIGHT }}>{cfg.tagline}</Typography>
        )}
        <Typography component="h2" sx={{ fontFamily: FONT, fontWeight: 700, fontSize: "22px", lineHeight: "28px", color: "#FFFFFF" }}>
          {cfg.title}
        </Typography>
        {cfg.subtitle && (
          <Typography sx={{ fontFamily: FONT, fontWeight: 500, fontSize: "12px", lineHeight: "16px", color: "rgba(255, 255, 255, 0.8)" }}>
            {cfg.subtitle}
          </Typography>
        )}
      </Box>
      <Box sx={{ px: isMobile ? "16px" : "24px", py: "20px" }}>{body}</Box>
    </Box>
  );

  // ── Donor details + payment (shared with DonationBoxV2) ─────────────────────
  if (detailsStep) {
    return shell(
      <SubscribeDetailsStep
        amount={detailsStep.amount}
        tipAmount={detailsStep.tipAmount}
        currency={INR}
        frequency={detailsStep.frequency}
        campaignId={detailsStep.campaignId}
        sourceCampaignId={sourceCampaignId}
        campaignTitle={detailsStep.label}
        donationCategory={detailsStep.category}
        contributionOptions={contributionOptions}
        contributionCategoryCodes={contributionCategoryCodes}
        showCategoryPicker={false}
        isPoolDonation={false}
        onceDonationCategory={detailsStep.frequency === "once" ? detailsStep.category : null}
        thankYouVariant="temple"
        theme={DETAILS_THEME}
        existingSubscriptionToast
        onBack={() => setDetailsStep(null)}
        onCheckoutDismiss={
          onCheckoutAbandoned
            ? (abandoned) => {
                // Incomplete checkout: stay on the page, back on the box's first screen.
                setDetailsStep(null);
                if (abandoned?.frequency !== "monthly") {
                  if (showToggle) setMode("once");
                  onCheckoutAbandoned(abandoned);
                  return;
                }
                // Closed a MONTHLY checkout: the reminder is only ever for a one-time gift.
                // When the box offers Donate Once, switch to it (same amount stays selected) and
                // arm the reminder as a one-time gift of that amount; otherwise no reminder.
                if (!showToggle) return;
                setMode("once");
                const fund = campaignId ? null : contributionCategoryCodes[0];
                if (!campaignId && !fund) return;
                const base = Number(abandoned.amount) || 0;
                const tip = Math.round((base * supportPct) / 100);
                const monthly = abandoned.additionalData || {};
                onCheckoutAbandoned({
                  kind: "money",
                  frequency: "once",
                  amount: base + tip,
                  tipAmount: tip,
                  form: abandoned.form,
                  // same shape SubscribeDetailsStep sends for a one-time gift
                  additionalData: {
                    currency: monthly.currency || INR.code,
                    state: monthly.state,
                    whatsappUpdates: monthly.whatsappUpdates,
                    campaignId: campaignId || null,
                    campaignName: campaignId ? campaignTitle : contributionOptions[0],
                    tipAmount: tip,
                    anonymousCheck: 0,
                    isPoolDonation: false,
                    ...(fund && { donationCategory: fund }),
                    ...(monthly.sourceCampaignId && { sourceCampaignId: monthly.sourceCampaignId }),
                    ...(monthly.thankYouVariant && { thankYouVariant: monthly.thankYouVariant }),
                  },
                });
              }
            : undefined
        }
      />
    );
  }

  const tab = (key, label, icon, iconActive) => {
    const active = (key === "once") === isOnce;
    return (
      <Box
        component="button"
        type="button"
        onClick={() => setMode(key)}
        aria-pressed={active}
        sx={{
          flex: 1,
          minWidth: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: "8px",
          px: "12px",
          py: "11px",
          borderRadius: "9px",
          border: "none",
          cursor: "pointer",
          background: active ? "#FFFFFF" : "transparent",
          boxShadow: active ? "0px 2px 6px 0px rgba(0, 0, 0, 0.08)" : "none",
          transition: "background 0.2s, box-shadow 0.2s",
          "&:focus-visible": { outline: `2px solid ${RED}`, outlineOffset: "2px" },
        }}
      >
        <Box component="img" src={active ? iconActive : icon} alt="" sx={{ width: 16, height: 16, p: "2px", boxSizing: "border-box" }} />
        <Typography sx={{ fontFamily: FONT, fontWeight: active ? 700 : 500, fontSize: "14px", lineHeight: "18px", color: active ? INK : MUTED, whiteSpace: "nowrap" }}>
          {label}
        </Typography>
      </Box>
    );
  };

  const chip = (amount, idx) => {
    const selected = selectedChipIdx === idx;
    const badge = amountButtons[idx]?.label;
    return (
      <Box
        key={amount}
        component="button"
        type="button"
        onClick={() => selectChip(idx)}
        aria-pressed={selected}
        sx={{
          position: "relative",
          height: "48px",
          borderRadius: "10px",
          border: selected ? `1px solid ${RED}` : "1px solid #E3E3E3",
          background: selected ? RED : "#FFFFFF",
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          transition: "background 0.15s, border-color 0.15s",
          "&:focus-visible": { outline: `2px solid ${RED}`, outlineOffset: "2px" },
        }}
      >
        <Rupee value={amount} sx={{ fontFamily: FONT, fontWeight: 700, fontSize: "15px", lineHeight: "20px", color: selected ? "#FFFFFF" : INK }} />
        {badge && (
          <Box
            sx={{
              position: "absolute",
              top: "-8px",
              left: "50%",
              transform: "translateX(-50%)",
              background: GOLD_LIGHT,
              borderRadius: "100px",
              px: "8px",
              py: "2px",
              whiteSpace: "nowrap",
            }}
          >
            <Typography sx={{ fontFamily: FONT, fontWeight: 700, fontSize: "9px", lineHeight: "12px", letterSpacing: "0.5px", color: MAROON, textTransform: "uppercase" }}>
              {badge}
            </Typography>
          </Box>
        )}
      </Box>
    );
  };

  return (
    <>
      {shell(
        <Box sx={{ display: "flex", flexDirection: "column", gap: "20px" }}>
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "12px" }}>
            {/* ── Once / Monthly toggle — only when admin offers both ─────────── */}
            {showToggle && (
              <Box sx={{ width: "100%", boxSizing: "border-box", background: "#F5F5F5", borderRadius: "12px", p: "4px", display: "flex", gap: "4px" }}>
                {tab("once", "Donate Once", onceHeartInactive, onceHeart)}
                {tab("monthly", "Donate Monthly", monthlyRepeat, monthlyRepeatActive)}
              </Box>
            )}

            {/* ── My contribution goes to (Temple sub-categories) — monthly only ── */}
            {!isOnce && (
            <Box sx={{ width: "100%", display: "flex", flexDirection: "column", gap: "6px" }}>
              <Typography sx={{ fontFamily: FONT, fontWeight: 600, fontSize: "14px", lineHeight: "18px", color: INK }}>
                {cfg.contributionLabel}
              </Typography>
              <Box
                component="button"
                type="button"
                disabled={!hasSubCategories}
                onClick={(e) => setContributionMenuAnchor(e.currentTarget)}
                sx={{
                  width: "100%",
                  boxSizing: "border-box",
                  background: "#FBF5EC",
                  border: "1px solid rgba(201, 149, 43, 0.4)",
                  borderRadius: "10px",
                  px: "14px",
                  py: "12px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: "8px",
                  cursor: hasSubCategories ? "pointer" : "default",
                  "&:focus-visible": { outline: `2px solid ${RED}`, outlineOffset: "2px" },
                }}
              >
                <Typography sx={{ fontFamily: FONT, fontWeight: 700, fontSize: "15px", lineHeight: "20px", color: hasSubCategories ? INK : MUTED, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {hasSubCategories ? selectedLabel : "No temple funds open right now"}
                </Typography>
                {hasSubCategories && <Box component="img" src={chevronDown} alt="" sx={{ width: 18, height: 18, flexShrink: 0 }} />}
              </Box>
              <Menu
                anchorEl={contributionMenuAnchor}
                open={Boolean(contributionMenuAnchor)}
                onClose={() => setContributionMenuAnchor(null)}
                sx={{ zIndex: 1600 }}
                PaperProps={{ sx: { borderRadius: "10px", width: contributionMenuAnchor?.offsetWidth, maxWidth: "none" } }}
              >
                {contributionOptions.map((option, idx) => (
                  <MenuItem
                    key={contributionCategoryCodes[idx]}
                    selected={safeIdx === idx}
                    onClick={() => { setSelectedContributionIdx(idx); setContributionMenuAnchor(null); }}
                    sx={{ fontFamily: FONT, fontSize: "14px", fontWeight: safeIdx === idx ? 700 : 500, color: safeIdx === idx ? RED : INK }}
                  >
                    {option}
                  </MenuItem>
                ))}
              </Menu>
            </Box>
            )}

            <SmallCaps>Choose an amount</SmallCaps>

            {/* ── Amount grid: 3 per row, "Other" in the last cell ──────────── */}
            <Box sx={{ width: "100%", pt: "6px", display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", columnGap: "10px", rowGap: "14px" }}>
              {chipAmounts.map(chip)}
              <Box
                component="button"
                type="button"
                onClick={enterOtherMode}
                aria-pressed={isOtherMode}
                sx={{
                  height: "48px",
                  borderRadius: "10px",
                  border: isOtherMode ? `1px solid ${RED}` : "1px solid #E3E3E3",
                  background: isOtherMode ? RED : "#FFFFFF",
                  cursor: "pointer",
                  fontFamily: FONT,
                  fontWeight: 700,
                  fontSize: "15px",
                  lineHeight: "20px",
                  color: isOtherMode ? "#FFFFFF" : INK,
                  "&:focus-visible": { outline: `2px solid ${RED}`, outlineOffset: "2px" },
                }}
              >
                Other
              </Box>
            </Box>

            {/* ── Amount input ─────────────────────────────────────────────── */}
            <Box
              sx={{
                width: "100%",
                boxSizing: "border-box",
                background: "#FBF5EC",
                border: "1px solid rgba(201, 149, 43, 0.4)",
                borderRadius: "10px",
                px: "14px",
                py: "12px",
                display: "flex",
                alignItems: "center",
                gap: "12px",
                "&:focus-within": { borderColor: "#C9952B" },
              }}
            >
              <Typography sx={{ fontFamily: FONT, fontWeight: 700, fontSize: "14px", lineHeight: "20px", color: INK, whiteSpace: "nowrap" }}>
                <Box component="span" sx={{ fontFamily: RUPEE_FONT }}>₹</Box> INR
              </Typography>
              <Box component="img" src={currencyChevron} alt="" sx={{ width: 8.17, height: 4.67, mx: "2.9px" }} />
              <Box sx={{ width: "1px", height: "22px", background: "#E3D2B5", flexShrink: 0 }} />
              <Box
                component="input"
                type="text"
                inputMode="numeric"
                aria-label="Amount in rupees"
                value={amountDisplay}
                onChange={handleAmountInput}
                onFocus={enterOtherMode}
                sx={{ flex: 1, minWidth: 0, border: "none", outline: "none", background: "transparent", fontFamily: FONT, fontWeight: 700, fontSize: "18px", lineHeight: "24px", color: INK, p: 0 }}
              />
              {!isOnce && cfg.monthlyLabel && (
                <Typography sx={{ fontFamily: FONT, fontWeight: 700, fontSize: "14px", lineHeight: "20px", color: "#C9952B", whiteSpace: "nowrap" }}>
                  {cfg.monthlyLabel}
                </Typography>
              )}
            </Box>

            {/* ── Support us by (one-time only) ────────────────────────────── */}
            {isOnce && (
              <Box sx={{ width: "100%", boxSizing: "border-box", background: "#F1F1F1", borderRadius: "10px", pl: "16px", pr: "8px", py: "8px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: "8px" }}>
                <Typography sx={{ fontFamily: FONT, fontWeight: 500, fontSize: "14px", lineHeight: "20px", color: INK, whiteSpace: "nowrap" }}>
                  Support us by :
                </Typography>
                <Box
                  component="button"
                  type="button"
                  onClick={(e) => setSupportMenuAnchor(e.currentTarget)}
                  sx={{ width: 133, height: 34, boxSizing: "border-box", background: "#FFFFFF", border: "1.114px solid #B9B9B8", borderRadius: "8.9px", px: "17.8px", display: "flex", alignItems: "center", justifyContent: "space-between", cursor: "pointer" }}
                >
                  <Typography sx={{ fontFamily: FONT, fontWeight: 600, fontSize: "12px", color: INK, whiteSpace: "nowrap" }}>
                    {supportPct}% - <Rupee value={supportAmount} />
                  </Typography>
                  <Box component="img" src={chevronDown} alt="" sx={{ width: 18, height: 18 }} />
                </Box>
                <Menu anchorEl={supportMenuAnchor} open={Boolean(supportMenuAnchor)} onClose={() => setSupportMenuAnchor(null)} sx={{ zIndex: 1600 }} PaperProps={{ sx: { borderRadius: "10px", minWidth: 140 } }}>
                  {SUPPORT_PCTS.map((pct) => (
                    <MenuItem key={pct} onClick={() => { setSupportPct(pct); setSupportMenuAnchor(null); }} sx={{ fontFamily: FONT, fontSize: "14px" }}>
                      {pct}%{pct > 0 ? <>&nbsp;-&nbsp;<Rupee value={Math.round((baseAmount * pct) / 100)} /></> : ""}
                    </MenuItem>
                  ))}
                </Menu>
              </Box>
            )}
          </Box>

          {/* ── Continue ─────────────────────────────────────────────────── */}
          <Box
            component="button"
            type="button"
            onClick={handleContinue}
            sx={{
              width: "100%",
              background: RED,
              border: "none",
              borderRadius: "12px",
              py: "16px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "10px",
              cursor: "pointer",
              boxShadow: "0px 10px 24px 0px rgba(236, 27, 38, 0.35)",
              transition: "filter 0.15s",
              "&:hover": { filter: "brightness(1.05)" },
              "&:active": { transform: "translateY(1px)" },
              "&:focus-visible": { outline: `2px solid ${INK}`, outlineOffset: "2px" },
            }}
          >
            <Typography sx={{ fontFamily: FONT, fontWeight: 700, fontSize: "16px", lineHeight: "20px", color: "#FFFFFF", whiteSpace: "nowrap" }}>
              {cfg.buttonLabel} · <Rupee value={totalAmount} />
            </Typography>
            <Box component="img" src={prayingHands} alt="" sx={{ width: 22, height: 22, p: "2.9px 3.9px 1.6px", boxSizing: "border-box" }} />
          </Box>

          {/* ── Donate via (both tabs: continues the selected Once / Monthly flow) ──── */}
          {(
            <Box sx={{ display: "flex", flexDirection: "column", gap: "10px" }}>
              <SmallCaps>Donate via</SmallCaps>
              <Box sx={{ background: "#3B3A3A", borderRadius: "12px", px: isMobile ? "10px" : "20px", py: "12px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                {PAYMENT_OPTIONS.map((option) => (
                  <Box
                    key={option.name}
                    component="button"
                    type="button"
                    onClick={handleContinue}
                    sx={{ width: 57, display: "flex", flexDirection: "column", alignItems: "center", gap: "10px", background: "transparent", border: "none", cursor: "pointer", p: 0, "&:hover": { opacity: 0.85 } }}
                  >
                    {option.icon}
                    <Typography sx={{ fontFamily: FONT, fontSize: "14px", lineHeight: "16.8px", color: "#FFFFFF", textAlign: "center" }}>{option.name}</Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          )}
        </Box>
      )}

      <Snackbar open={snackbarOpen} autoHideDuration={4000} onClose={() => setSnackbarOpen(false)} anchorOrigin={{ vertical: "bottom", horizontal: "center" }} sx={{ zIndex: 1700 }}>
        <Alert onClose={() => setSnackbarOpen(false)} severity="error" sx={{ width: "100%" }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default TempleDonationBox;
